import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";
import {
    BlurEvent, Component, DataSource, DataSourceMode, DataSourceModeChangeEvent, Image, Label, Panel, Switch, TableRow, Textbox
} from "@mcleod/components";
import { DatePart, DateUtil, ModelRow } from "@mcleod/core";
import { OrderStop } from "./OrderStop";
import { getDispatchControlBoolean } from "@mcleod/general";
import { PlanningUtil } from "./planning/PlanningUtil";

interface RevertedComponents {
    schedArriveEarly?: Textbox;
    schedArriveLate?: Textbox;
    switchConfirmed?: Switch;
}

export type IntermediateAppointmentProps = {
    tableRow: TableRow;
    imageRequiredId: string;
    labelRequiredId: string;
    requiredField: string;
    imageConfirmedId: string;
    labelConfirmedId: string;
    confirmedField: string;
    createClickListener?: boolean;
}

export class AppointmentUtil {
    static async checkAppointment(event: BlurEvent, orderStopLayout?: OrderStop) {
        const dateTextbox: Textbox = event.target as Textbox;
        const row: ModelRow = dateTextbox.getRelevantModelRow();
        // the isDropDownVisible() is accounting for when you change dates and the dropdown is visible; BlurEvents get triggered on those events as well
        if (dateTextbox.isDropdownVisible() || dateTextbox.validationWarning != null ||
            row._appending || orderStopLayout?.validateApptChanges === false || !getDispatchControlBoolean("appt_confirmation", false) ||
            !this.isApptConfirmed(row) || this.hasAppointmentRecord(row)) {
            return;
        }
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target as Component) ?? null; // get the table row if the event is from a table row
        const originalData: ModelRow = row.originalData;
        const oldSchedArriveEarly = originalData["sched_arrive_early"];
        const oldSchedArriveLate = originalData["sched_arrive_late"];
        const datesChanged = this.checkChangedDates(row, originalData);
        const datesNotNull = row.get("sched_arrive_early") != null || row.get("sched_arrive_late") != null;
        let compsToRevert: RevertedComponents = null;
        if (tableRow != null || orderStopLayout != null) {
            compsToRevert = this.gatherComponentsToRevert(tableRow, orderStopLayout);
        }
        if ((datesChanged && datesNotNull)) {
            if (dateTextbox.field == "sched_arrive_late" || dateTextbox.field == "sched_arrive_early") {
                if (orderStopLayout != null) {
                    // OrderStop Workflow
                    await this.showAppointmentChangeDialog({ [dateTextbox.field]: dateTextbox.field == "sched_arrive_late" ? oldSchedArriveLate : oldSchedArriveEarly }, orderStopLayout.sourceAppointmentChange, row, compsToRevert);
                } else {
                    // MultiStop || StopEntry workflow
                    await this.showAppointmentChangeDialog({ [dateTextbox.field]: dateTextbox.field == "sched_arrive_late" ? oldSchedArriveLate : oldSchedArriveEarly }, null, row, compsToRevert, tableRow);
                }
            }
        }
    }

    static gatherComponentsToRevert(tableRow: TableRow, orderStopLayout: OrderStop): RevertedComponents {
        return {
            schedArriveEarly: tableRow?.findComponentById("textboxSchedArriveEarly") as Textbox ?? orderStopLayout.textboxSchedArriveEarly,
            schedArriveLate: tableRow?.findComponentById("textboxSchedArriveLate") as Textbox ?? orderStopLayout.textboxSchedArriveLate,
            switchConfirmed: tableRow?.findComponentById("switchConfirmed") as Switch ?? orderStopLayout?.switchConfirmed ?? null
        }
    }

    static checkChangedDates(row: ModelRow, originalData: ModelRow): boolean {
        const schedArriveEarly = row.get("sched_arrive_early");
        const schedArriveLate = row.get("sched_arrive_late", null);
        const oldSchedArriveEarly = originalData["sched_arrive_early"];
        const oldSchedArriveLate = originalData["sched_arrive_late"];
        const arriveDiff = DateUtil.dateDiff(DatePart.SECOND, new Date(schedArriveEarly), new Date(oldSchedArriveEarly)) != 0;
        const lateDiff = DateUtil.dateDiff(DatePart.SECOND, new Date(schedArriveLate), new Date(oldSchedArriveLate)) != 0
        if (schedArriveLate == null && arriveDiff ||
            schedArriveLate != null && (lateDiff || arriveDiff)) {
            return true;
        }
        return false;
    }

    static async showAppointmentChangeDialog(oldValues: any, sourceApptChange: DataSource, modelRow: ModelRow, compsToRevert: RevertedComponents = null, tableRow?: TableRow) {
        const panel = new Panel({ id: "apptChangePanel" });
        const reasonCodeTextbox = new Textbox({
            required: true, id: "textboxReasonCode", caption: "Reason code",
            dataSource: sourceApptChange, field: "reason_code",
            lookupModel: "lme/dispatch/edi-partner-code", lookupModelLayout: "lme/dispatch/EdiPartnerCode",
            lookupModelDisplayField: "description", lookupModelResultField: "standard_code", lookupModelMinChars: 2
        }) as Textbox;

        reasonCodeTextbox.addBeforeLookupModelSearchListener(event => {
            event.filter.stop_id = modelRow.get("id");
            event.filter.code_type = "DelayReason";
            event.filter.use_enabled_flag = "Y";
        });

        const descriptionTextBox = new Textbox({ id: "textboxDescription", caption: "Description", dataSource: sourceApptChange, field: "descr" });
        const contactTextBox = new Textbox({ id: "textboxContact", caption: "Contact", dataSource: sourceApptChange, field: "contact" });
        panel.add(reasonCodeTextbox, descriptionTextBox, contactTextBox);

        if (sourceApptChange != null) {
            this.setSourceAppointmentChangeModeAdd(sourceApptChange);
        }

        const confirmed = await CommonDialogs.showYesNo(panel, "Change Appointment", { xVisible: false, noButtonCaption: "Cancel", yesButtonCaption: "OK" }, false, 100)

        const oldApptChangeData = sourceApptChange?.data;
        if (confirmed) {
            if (sourceApptChange == null) {
                // MultiStop || StopEntry Case
                const data = {
                    stop_id: modelRow.get("id"),
                    descr: descriptionTextBox.valueAsString,
                    contact: contactTextBox.valueAsString,
                    reason_code: reasonCodeTextbox.getFirstLookupModelData().get("standard_code", ""),
                    sequence: modelRow.get("order_sequence"),
                }
                const newApptChangeRow = new ModelRow("lme/dispatch/appointment-change", false, data);
                if (tableRow != null)
                    tableRow["appointmentChangeData"] = newApptChangeRow;
                else {
                    modelRow.set("appointment_change", newApptChangeRow);
                }
            } else {
                modelRow.set("appointment_change", sourceApptChange.activeRow);
            }
        } else {
            if (sourceApptChange == null) {
                // MultiStop || StopEntry Case
                this.revertApptChanges(oldValues, null, null, compsToRevert, modelRow);
            } else {
                // OrderStop Case
                this.revertApptChanges(oldValues, oldApptChangeData, sourceApptChange, compsToRevert, modelRow);
            }
        }

    }

    static revertApptChanges(oldValues: any, oldApptChangeData: any, dataSource: DataSource, compsToRevert: RevertedComponents, modelRow?: ModelRow,) {
        modelRow?.setValues(oldValues);
        if (compsToRevert != null) {
            const apptComps: Component[] = [compsToRevert.schedArriveEarly, compsToRevert.schedArriveLate, compsToRevert.switchConfirmed];
            apptComps.forEach(comp => {
                if (!!comp)
                    comp.displayData(modelRow, null, 0);
            });
        }
        if (dataSource != null) {
            dataSource.activeRow.setValues(oldApptChangeData);
        }
    }

    static isApptConfirmed(modelRow: ModelRow): boolean {
        return modelRow?.get("confirmed") == "Y";
    }

    static hasAppointmentRecord(modelRow: ModelRow): boolean {
        return !modelRow.isNull("appointment_change");
    }

    static apptConfirmCheck(changedData: any, modelRow: ModelRow): boolean {
        // Scenario 1: If confirmed was not set to Y initially and current confirmed status is Y, then post appointment_change record
        // Scenario 2: If confirmed was initially Y and set to N, check to see that current confirmed status is 'N'
        // Scenario 3: If confirmed was set to Y initially and only dates were changed
        // We have these scenarios to account for showing the appointment change dialog on dates after the user clicks confirm or
        // if the stop was already confirmed
        if ((changedData["confirmed"] != null && changedData["confirmed"] === "Y") && modelRow.get("confirmed") == 'Y') {
            // In Scenario 1, confirmed was not set to Y initially, thus we need to create another initial confirmation record showing it was set to confirm
            // This can be a blank appointment change record, a dialog does not need to be created;
            const initialAppointmentChangeData = {
                stop_id: modelRow.get("id"),
                descr: "",
                contact: "",
                reason_code: "",
                sequence: modelRow.get("order_sequence"),
                revised_early: modelRow.get("sched_arrive_early"),
                revised_late: modelRow.get("sched_arrive_late")
            };
            modelRow.set("initial_appointment_change", new ModelRow("lme/dispatch/appointment-change", false, initialAppointmentChangeData));
            return true;
        }
        // Scenario 2
        else if ((changedData["confirmed"] != null && changedData["confirmed"] === "N") && modelRow.get("confirmed") == 'N') {
            return true;
        }
        // Scenario 3
        else if (changedData["sched_arrive_late"] != null || changedData["sched_arrive_early"] != null && modelRow.get("confirmed") == 'Y') {
            return true;
        }
        return false;
    }

    static setSourceAppointmentChangeModeAdd(sourceAppointmentChange: DataSource): Promise<Boolean> {
        return new Promise((resolve) => {
            if (sourceAppointmentChange.mode !== DataSourceMode.ADD) {
                sourceAppointmentChange.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
                    if (event.newMode == DataSourceMode.ADD)
                        resolve(true);
                });
                sourceAppointmentChange.mode = DataSourceMode.ADD;
            } else {
                resolve(true);
            }
        })
    }

    static getRequiredConfirmed(modelRow: ModelRow, requiredField: string, confirmedField: string): { isApptRequired: boolean, isApptConfirmed: boolean } {
        const isApptRequired = modelRow.getBoolean(requiredField);
        const isApptConfirmed = modelRow.getBoolean(confirmedField);
        return { isApptRequired, isApptConfirmed };
    }

    static setAppointmentLabel(tableRow: TableRow, labelId: string, requiredField: string, confirmedField: string, ) {
        const modelRow: ModelRow = tableRow.data;
        const labelAppt = tableRow.findComponentById(labelId) as Label;
        if (labelAppt == null) return;
        const { isApptRequired, isApptConfirmed } = this.getRequiredConfirmed(modelRow, requiredField, confirmedField);
        if (isApptRequired) {
            if (isApptConfirmed) {
                labelAppt.text = "Confirmed";
                labelAppt.imageColor = "green";
                labelAppt.imageName = ImageName.appointmentConfirmed;
                labelAppt.tooltip = "Appointment Confirmed";
            } else {
                labelAppt.text = "Required";
                labelAppt.imageColor = "orange";
                labelAppt.imageName = ImageName.appointmentRequired;
                labelAppt.tooltip = "Appointment Required";
            }
        }
        labelAppt.visible = isApptRequired;
    }

    static setAppointmentImageForTableRow(tableRow: TableRow, imageId: string, requiredField: string, confirmedField: string) {
        const modelRow: ModelRow = tableRow.data;
        const imageAppt = tableRow.findComponentById(imageId) as Image;
        if (imageAppt == null) return;
        this.setAppointmentImage(imageAppt, modelRow, requiredField, confirmedField);
    }

    static setIntermediateAppointmentImageAndLabel(props: IntermediateAppointmentProps) {
        setIntermediateAppointmentComponents(props.tableRow, props.imageRequiredId, props.labelRequiredId, props.requiredField, props.createClickListener);
        setIntermediateAppointmentComponents(props.tableRow, props.imageConfirmedId, props.labelConfirmedId, props.confirmedField, props.createClickListener);

        function setIntermediateAppointmentComponents (tableRow: TableRow, imageId: string, labelId: string, fieldName: string, createClickListener: boolean = false) {
            const imageApptCount = tableRow.findComponentById(imageId) as Image;
            const labelApptCount = tableRow.findComponentById(labelId) as Label;
            if (imageApptCount != null && labelApptCount != null) {
                const apptCount = tableRow.data.get(fieldName, 0);
                imageApptCount.visible = labelApptCount.visible = apptCount > 0;
                labelApptCount.text = apptCount.toString();
                if (imageApptCount.visible && createClickListener) {
                    imageApptCount.addClickListener(_ => {
                        PlanningUtil.showMultiStopSlideout(imageApptCount, true)
                    });
                }
            }
        }
    }

    static setAppointmentImage(imageAppt: Image, modelRow: ModelRow, requiredField: string, confirmedField: string) {
        if (imageAppt == null) return;
        const { isApptRequired, isApptConfirmed } = this.getRequiredConfirmed(modelRow, requiredField, confirmedField);
        if (isApptRequired) {
            if (isApptConfirmed) {
                imageAppt.color = "green";
                imageAppt.name = ImageName.appointmentConfirmed;
                imageAppt.tooltip = "Appointment Confirmed";
            } else {
                imageAppt.color = "orange";
                imageAppt.name = ImageName.appointmentRequired;
                imageAppt.tooltip = "Appointment Required";
            }
        }
        imageAppt.visible = isApptRequired;
    }
}
